import { useState } from "react";
import { Copy, Check, WhatsappLogo, X, LinkSimple, Tag, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactDOM from "react-dom";

const InviteLinkModal = ({ isOpen, onClose, category, chapterName, chapterId, inviterName, meetingId, inviterId }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const baseUrl = window.location.origin;
  const params = new URLSearchParams({
    chapter_id: chapterId,
    chapter: chapterName,
    category: category,
    ...(meetingId && { meeting_id: meetingId }),
    ...(inviterName && { inviter: inviterName }),
    ...(inviterId && { inviter_id: inviterId }),
  });
  const inviteUrl = `${baseUrl}/signup?${params.toString()}`;
  const whatsappText = `Hey! We have an opening for *${category}* in our BCH chapter *${chapterName}*. Come experience the meeting and unlock maximum potential for business growth through referrals!\n\nRegister here: ${inviteUrl}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="invite-link-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-[var(--border-color)] rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted hover:text-primary transition-colors"
          data-testid="invite-modal-close"
        >
          <X className="w-5 h-5" weight="bold" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-bch-teal to-emerald-500 flex items-center justify-center shadow-lg">
            <LinkSimple className="w-5 h-5 text-white" weight="bold" />
          </div>
          <div>
            <h3 className="text-primary font-semibold text-lg">Invite Link</h3>
            <p className="text-muted text-xs">Share this link to invite a member</p>
          </div>
        </div>

        {/* Info */}
        <div className="bg-surface rounded-xl border border-[var(--border-color)] p-4 mb-5 space-y-2">
          <div className="flex items-center gap-2">
            <Buildings className="w-4 h-4 text-bch-teal shrink-0" weight="duotone" />
            <span className="text-primary text-sm font-medium">{chapterName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-bch-gold shrink-0" weight="fill" />
            <span className="text-primary text-sm font-semibold">{category}</span>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-bch-teal font-semibold px-2 py-0.5 bg-bch-teal/10 rounded-md">Open</span>
          </div>
        </div>

        {/* Link Box */}
        <div className="bg-surface rounded-xl border border-[var(--border-color)] p-3 mb-5">
          <p className="text-muted text-[11px] uppercase tracking-wider font-semibold mb-2">Invite URL</p>
          <div className="flex items-center gap-2">
            <p className="text-primary text-xs font-mono break-all flex-1 select-all leading-relaxed" data-testid="invite-link-url">
              {inviteUrl}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleCopy}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
              copied
                ? "bg-emerald-500 text-white"
                : "bg-gradient-to-r from-bch-teal to-emerald-500 text-white hover:shadow-lg hover:shadow-bch-teal/20"
            }`}
            data-testid="invite-copy-btn"
          >
            {copied ? <Check className="w-4 h-4" weight="bold" /> : <Copy className="w-4 h-4" weight="bold" />}
            {copied ? "Copied!" : "Copy Link"}
          </button>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#25D366] text-white font-medium text-sm hover:bg-[#1EBE5A] transition-colors"
            data-testid="invite-whatsapp-btn"
          >
            <WhatsappLogo className="w-5 h-5" weight="fill" />
            WhatsApp
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InviteLinkModal;
